import { createContext, useCallback, useEffect, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { Hub, HubCallback } from '@aws-amplify/core';

import { AuthState } from '../enum';
import { useContextFallback } from '../hooks';
import { AuthContextState, CognitoUser } from '../models';

type AuthContextType = AuthContextState & {
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  confirmSignIn: (
    mfaType: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA',
    code: string
  ) => Promise<void>;
  completeNewPassword: (password: string) => Promise<void>;
  changePassword: (
    oldPassword: string,
    newPassword: string
  ) => Promise<'SUCCESS'>;
  getJwtToken: () => Promise<string>;
  validateEmail: () => Promise<void>;
  completeValidateEmail: (code: string) => Promise<void>;
};

const AUTH_STATE_ERROR = 'Incorrect auth state.';

const AuthContext = createContext<AuthContextType | undefined>(undefined);
AuthContext.displayName = 'AuthContext';

type AuthProviderProps = {
  initialState?: AuthContextState;
  onSignOut?: () => void;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  initialState = {
    state: AuthState.Loading,
  },
  onSignOut,
}) => {
  const [authState, setAuthState] = useState<AuthContextState>(initialState);

  const handleSignIn = useCallback(async () => {
    const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser({
      bypassCache: true,
    });

    if (cognitoUser.attributes.email_verified) {
      setAuthState({
        state: AuthState.SignedIn,
        cognitoUser,
      });
    } else {
      setAuthState({
        state: AuthState.VerifyEmail,
        cognitoUser,
      });
    }
  }, []);

  const initAuth = useCallback(async () => {
    try {
      await handleSignIn();
    } catch (error) {
      if (error === 'The user is not authenticated') {
        setAuthState({
          state: AuthState.SignIn,
          initial: true,
        });
      } else {
        setAuthState({
          state: AuthState.Error,
          error,
        });
      }
    }
  }, [handleSignIn]);

  const signIn = async (username: string, password: string) => {
    const cognitoUser: CognitoUser = await Auth.signIn(username, password);

    if (cognitoUser.challengeName) {
      setAuthState({
        state: AuthState.Challenge,
        cognitoUser,
        challengeName: cognitoUser.challengeName,
      });
    }
  };

  const signOut = async () => {
    await Auth.signOut();

    if (onSignOut) {
      onSignOut();
    }
  };

  const completeNewPassword = async (password: string) => {
    /* istanbul ignore next */
    if (authState.state !== AuthState.Challenge) {
      throw new Error(AUTH_STATE_ERROR);
    }

    const cognitoUser: CognitoUser = await Auth.completeNewPassword(
      authState.cognitoUser,
      password
    );

    if (cognitoUser.challengeName) {
      setAuthState({
        state: AuthState.Challenge,
        cognitoUser,
        challengeName: cognitoUser.challengeName,
      });
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    if (authState.state !== AuthState.SignedIn) {
      throw new Error(AUTH_STATE_ERROR);
    }

    return Auth.changePassword(authState.cognitoUser, oldPassword, newPassword);
  };

  const confirmSignIn = async (
    mfaType: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA',
    code: string
  ) => {
    /* istanbul ignore next */
    if (authState.state !== AuthState.Challenge) {
      throw new Error(AUTH_STATE_ERROR);
    }
    await Auth.confirmSignIn(authState.cognitoUser, code, mfaType);
  };

  const getJwtToken = useCallback(async () => {
    try {
      return (await Auth.currentSession()).getAccessToken().getJwtToken();
    } catch (error: any) {
      if (error.code === 'NotAuthorizedException') {
        await Auth.signOut();
      }

      throw error;
    }
  }, []);

  const validateEmail = useCallback(async () => {
    await Auth.verifyCurrentUserAttribute('email');
  }, []);

  const completeValidateEmail = async (code: string) => {
    /* istanbul ignore next */
    if (authState.state !== AuthState.VerifyEmail) {
      throw new Error('Incorrect auth state.');
    }

    await Auth.verifyCurrentUserAttributeSubmit('email', code);

    setAuthState({
      state: AuthState.SignedIn,
      cognitoUser: authState.cognitoUser,
    });
  };

  useEffect(() => {
    const listener: HubCallback = ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          handleSignIn();
          break;
        case 'signOut':
          setAuthState({
            state: AuthState.SignIn,
            initial: false,
          });
          break;
      }
    };

    Hub.listen('auth', listener);

    return () => {
      Hub.remove('auth', listener);
    };
  }, [handleSignIn]);

  useEffect(() => {
    if (authState.state === AuthState.Loading) {
      initAuth();
    }
  }, [initAuth, authState.state]);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        signIn,
        signOut,
        confirmSignIn,
        completeNewPassword,
        changePassword,
        getJwtToken,
        validateEmail,
        completeValidateEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContextFallback(AuthContext);
